import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useReducer,
  ReactNode,
} from 'react'

interface MouseContextTypes {
  clientX: number
  clientY: number
}

interface ScreenContextTypes {
  innerHeight: number
  innerWidth: number
}

interface reducerState {
  globalLoading: boolean
  isRightNavHeaderInputModalOpen: boolean
  isPostOverlayOpen: boolean
}

interface mainGlobalContextPropsTypes {
  state: reducerState
  dispatch: React.Dispatch<Action>
}

const defaultMouseState = {
  clientX: 0,
  clientY: 0,
}

const defaultScreenState = {
  innerHeight: 0,
  innerWidth: 0,
}

const initialReducerState: reducerState = {
  globalLoading: true,
  isRightNavHeaderInputModalOpen: false,
  isPostOverlayOpen: false,
}

const MouseContext = createContext<MouseContextTypes>(defaultMouseState)
const ScreenContext = createContext<ScreenContextTypes>(defaultScreenState)
const mainGlobalContext = createContext<mainGlobalContextPropsTypes>({
  state: initialReducerState,
  dispatch: () => {},
})

export type Action =
  | { type: 'OPEN_RIGHT_NAV_INPUT_MODAL' }
  | { type: 'CLOSE_RIGHT_NAV_INPUT_MODAL' }
  | { type: 'GLOBAL_CONTEXT_LOADING_COMPLETE' }
  | { type: 'OPEN_POST_OVERLAY' }
  | { type: 'CLOSE_POST_OVERLAY' }

const reducer = (state: reducerState, action: Action): reducerState => {
  switch (action.type) {
    case 'GLOBAL_CONTEXT_LOADING_COMPLETE':
      return { ...state, globalLoading: false }
    case 'OPEN_RIGHT_NAV_INPUT_MODAL':
      return { ...state, isRightNavHeaderInputModalOpen: true }
    case 'CLOSE_RIGHT_NAV_INPUT_MODAL':
      return { ...state, isRightNavHeaderInputModalOpen: false }
    case 'OPEN_POST_OVERLAY':
      return { ...state, isPostOverlayOpen: true }
    case 'CLOSE_POST_OVERLAY':
      return { ...state, isPostOverlayOpen: false }
    default:
      return state
  }
}

export const GlobalContextProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const [state, dispatch] = useReducer(reducer, initialReducerState)
  const [screenSizeValues, setScreenSizeValues] =
    useState<ScreenContextTypes>(defaultScreenState)
  const [mouseClickValues, setMouseClickValues] =
    useState<MouseContextTypes>(defaultMouseState)

  useEffect(() => {
    const getScreenSizeValues = () => {
      const { innerHeight, innerWidth } = window
      setScreenSizeValues({ innerHeight: innerHeight, innerWidth: innerWidth })
    }

    const getMouseClickValues = (event: MouseEvent) => {
      const { clientX, clientY } = event
      setMouseClickValues({ clientX: clientX, clientY: clientY })
    }

    getScreenSizeValues()

    window.addEventListener('resize', getScreenSizeValues)
    window.addEventListener('click', getMouseClickValues)

    dispatch({ type: 'GLOBAL_CONTEXT_LOADING_COMPLETE' })

    return () => {
      window.addEventListener('resize', getScreenSizeValues)
      window.removeEventListener('click', getMouseClickValues)
    }
  }, [])

  return (
    <mainGlobalContext.Provider value={{ state, dispatch }}>
      <ScreenContext.Provider value={screenSizeValues}>
        <MouseContext.Provider value={mouseClickValues}>
          {children}
        </MouseContext.Provider>
      </ScreenContext.Provider>
    </mainGlobalContext.Provider>
  )
}

export const useMainGlobalContext = () => useContext(mainGlobalContext)
export const useScreenContext = () => useContext(ScreenContext)
export const useMouseContext = () => useContext(MouseContext)
